import { useEffect, useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { may, useActiveTenant, useTenants } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/** The sign the mockup prints after the code — `INR ₹`, `USD $`. */
const CURRENCY_SIGNS: Record<string, string> = { INR: '₹', USD: '$' }

/**
 * Time zones offered in the picker.
 *
 * A short list rather than every IANA zone: AugRank is a Hyderabad product with
 * Indian and international tenants, all times are `Asia/Kolkata` by default
 * (CLAUDE.md), and a 400-entry select is a worse answer than a handful plus the
 * one the tenant already has. Any zone the API accepts stays valid — the
 * current value is added to the list below if it is not already in it, so a
 * tenant set to something unusual by support does not silently get moved.
 *
 * The label carries the common abbreviation where the zone has one (SHELL-23):
 * S-D3 writes `Asia/Kolkata (IST)`, and *IST* is what the tenant calls it.
 * Zones with no unambiguous short name are listed under the IANA id alone
 * rather than given an invented one.
 */
const COMMON_TIMEZONES: { id: string; label: string }[] = [
  { id: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { id: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { id: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { id: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { id: 'America/New_York', label: 'America/New_York (ET)' },
  { id: 'America/Los_Angeles', label: 'America/Los_Angeles (PT)' },
  { id: 'Australia/Sydney', label: 'Australia/Sydney (AET)' },
  { id: 'UTC', label: 'UTC' },
]

/**
 * Name and time zone. The slug is shown and not editable, because it is not.
 *
 * Read-only for a member: `tenant:update` is owner and admin only
 * (`api/app/tenancy/roles.py`), and the panel says so rather than presenting
 * fields that will 403 on save.
 *
 * **The fields are the mockup's** (parity P-SET-02, 2026-09-13): Business
 * name beside GSTIN, the billing address under them, Time zone beside
 * Currency. GSTIN and the address are the billing profile's one record —
 * `GET`/`PUT /api/v1/billing/tax-details`, whose GSTIN is checksum-validated
 * on the server — read and written from here as well as from `/billing`. The
 * earlier reading (SHELL-7/8) linked to Billing instead; the owner chose the
 * drawing, and the same request from two screens is still one record. A role
 * without `billing:read` sees the two fields disabled and empty, because the
 * read itself is refused.
 *
 * **The currency is a select with one option, disabled** (SHELL-9 kept in
 * substance). `budgets.currency` prices every metered event and picks the
 * payment provider (`app/billing/plans.PROVIDER_FOR`), so it cannot be
 * changed from a settings form; the control is the mockup's `ar-select`, and
 * it offers exactly the currency the organisation has.
 *
 * *Short name* is an app extra the mockup does not draw — kept, read-only,
 * without its help line.
 */
export function OrganisationPanel(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const { reload } = useTenants()
  const toast = useToast()
  const editable = may(membership, 'tenant:update')
  const seesBilling = may(membership, 'billing:read')

  const [name, setName] = useState(tenant.name)
  const [timezone, setTimezone] = useState(tenant.timezone)
  const [gstin, setGstin] = useState<string | null>(null)
  const [address, setAddress] = useState<string | null>(null)
  const [nameMissing, setNameMissing] = useState(false)

  // The currency lives on the budget row, and this is the only thing read from
  // it here. A failure is deliberately not surfaced: the field degrades to the
  // sentence without the code, and an error banner over the organisation's name
  // because a *budget* read failed would be reporting the wrong thing.
  const budget = useQuery((signal) => call(() => api.GET('/api/v1/usage/budget', { signal })), [])
  const currency = budget.state.data?.budget.currency ?? null

  // The tax record, for a role allowed to read it. Mounted unconditionally
  // (the hook must be), fetched only when the read would not be refused.
  const tax = useQuery(
    (signal) =>
      seesBilling
        ? call(() => api.GET('/api/v1/billing/tax-details', { signal }))
        : Promise.resolve({ ok: true as const, data: null }),
    [seesBilling],
  )
  const savedGstin = tax.state.data?.gstin ?? ''
  const savedAddress = tax.state.data?.billing_address ?? ''
  const editsTax = editable && may(membership, 'billing:manage')

  // Re-seeded when the organisation changes under the panel — a switch from the
  // sidebar keeps this component mounted, and without this the form would show
  // the previous organisation's name against the new one's id.
  useEffect(() => {
    setName(tenant.name)
    setTimezone(tenant.timezone)
    setGstin(null)
    setAddress(null)
  }, [tenant.id, tenant.name, tenant.timezone])

  const save = useMutation((body: { name: string; timezone: string }) =>
    call(() =>
      api.PATCH('/api/v1/tenants/{tenant_id}', {
        params: { path: { tenant_id: tenant.id } },
        body,
      }),
    ),
  )

  const zones = COMMON_TIMEZONES.some((zone) => zone.id === tenant.timezone)
    ? COMMON_TIMEZONES
    : [{ id: tenant.timezone, label: tenant.timezone }, ...COMMON_TIMEZONES]

  const saveTax = useMutation((body: { gstin: string | null; billing_address: string | null }) =>
    call(() => api.PUT('/api/v1/billing/tax-details', { body })),
  )

  const taxDirty =
    editsTax &&
    ((gstin !== null && gstin.trim() !== savedGstin) ||
      (address !== null && address.trim() !== savedAddress))

  // Enabled as drawn (parity P-SET-04): the mockup's *Save changes* is a
  // primary button, not a pale one waiting for a keystroke. A save with nothing
  // changed writes nothing and says so; an empty name is refused on the click.
  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (name.trim().length === 0) {
      setNameMissing(true)
      return
    }
    setNameMissing(false)
    const orgDirty = name.trim() !== tenant.name || timezone !== tenant.timezone
    if (!orgDirty && !taxDirty) {
      toast.show({ kind: 'info', title: 'Nothing to save' })
      return
    }
    if (orgDirty) {
      const result = await save.run({ name: name.trim(), timezone })
      if (!result.ok) return
      reload()
    }
    if (taxDirty) {
      const result = await saveTax.run({
        gstin: (gstin ?? savedGstin).trim() || null,
        billing_address: (address ?? savedAddress).trim() || null,
      })
      if (!result.ok) return
      tax.refetch()
      setGstin(null)
      setAddress(null)
    }
    toast.show({ kind: 'success', title: 'Organisation saved' })
  }

  return (
    <form
      className="ar-card"
      // Our own message under the field rather than the browser's tooltip, so
      // the refusal is drawn with the design's error state.
      noValidate
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <div className="ar-card__header">
        <h2>Organisation</h2>
        {editable ? null : <span className="ar-badge ar-badge--neutral">Read only</span>}
      </div>

      <div className="ar-card__body ar-stack">
        <InlineError failure={save.state.failure ?? saveTax.state.failure} />

        <div className="ar-grid ar-grid--2">
          <TextField
            label="Business name"
            required
            maxLength={120}
            disabled={!editable}
            value={name}
            error={nameMissing ? 'Give the organisation a name.' : undefined}
            onChange={(event) => {
              setName(event.target.value)
              setNameMissing(false)
              save.reset()
            }}
          />
          <Field label="GSTIN">
            {(aria) => (
              <input
                {...aria}
                className="ar-input ar-mono"
                disabled={!editsTax || tax.state.status === 'loading'}
                value={gstin ?? savedGstin}
                onChange={(event) => {
                  setGstin(event.target.value)
                  saveTax.reset()
                }}
              />
            )}
          </Field>
        </div>

        <Field label="Billing address">
          {(aria) => (
            <textarea
              id={aria.id}
              aria-describedby={aria['aria-describedby']}
              className="ar-textarea"
              rows={2}
              disabled={!editsTax || tax.state.status === 'loading'}
              value={address ?? savedAddress}
              onChange={(event) => {
                setAddress(event.target.value)
                saveTax.reset()
              }}
            />
          )}
        </Field>

        <div className="ar-grid ar-grid--2">
          <Field
            label="Time zone"
            help="All schedules and reports use this, never the server clock."
          >
            {(aria) => (
              <select
                className="ar-select"
                {...aria}
                disabled={!editable}
                value={timezone}
                onChange={(event) => {
                  setTimezone(event.target.value)
                  save.reset()
                }}
              >
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Currency">
            {(aria) => (
              <select className="ar-select" {...aria} disabled value={currency ?? ''}>
                <option value={currency ?? ''}>
                  {currency === null ? '—' : `${currency} ${CURRENCY_SIGNS[currency] ?? ''}`.trim()}
                </option>
              </select>
            )}
          </Field>
        </div>

        <div className="ar-grid ar-grid--2">
          <TextField label="Short name" readOnly value={tenant.slug} />
        </div>

        {/* SHELL-26. The role's own name is not printed here any more: it is
            whatever the organisation called it, and this sentence is about what
            the reader can do rather than about what they are called. */}
        {editable ? null : (
          <p className="ar-field__help" style={{ margin: 0 }}>
            Your role can view the organisation profile. A role granted the organisation settings
            page can change it.
          </p>
        )}
      </div>

      {editable ? (
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button
            tone="primary"
            type="submit"
            writes
            busy={save.pending || saveTax.pending}
            busyLabel="Saving"
          >
            Save changes
          </Button>
        </div>
      ) : null}
    </form>
  )
}
